import React, { useEffect, useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Slide } from './utils';
import Checkbox from '../../../../common/Checkbox';
import DataTable from '../../../../common/DataTable';

type SelectSlideTableProps = {
  slides: Slide[];
  initialSelectedSlideIds: any[];
  setValue: (field: string, value: any) => void;
};

const SelectSlideTable = ({ slides, initialSelectedSlideIds, setValue }: SelectSlideTableProps) => {
  const [selectedSlideIds, setSelectedSlideIds] = useState(initialSelectedSlideIds);
  const isInitialMount = React.useRef(true);

  // Only update from initialSelectedSlideIds on first mount
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      setSelectedSlideIds(initialSelectedSlideIds);
    }
  }, [initialSelectedSlideIds]);

  // Memoize slides to prevent DataTable reinitialization when array reference changes
  // Compare by stringifying IDs to detect actual content changes
  const slidesKey = useMemo(() => slides.map(s => s.id).join(','), [slides]);
  const memoizedSlides = useMemo(() => slides, [slidesKey]);

  // Calculate bulk checked state based on selected slides
  const bulkChecked = useMemo(() => {
    if (selectedSlideIds.length === 0) return 'none';
    const allSelected = memoizedSlides.every(slide => selectedSlideIds.includes(parseInt(slide.id as string, 10)));
    return allSelected ? 'all' : 'some';
  }, [selectedSlideIds, memoizedSlides]);

  const handleBulkCheckboxChangeCallback = useCallback(() => {
    setSelectedSlideIds((prevArray) => {
      if (prevArray.length > 0 && memoizedSlides.every(slide => prevArray.includes(parseInt(slide.id as string, 10)))) {
        // All are selected, so uncheck all
        return [];
      } else {
        // Check all
        return memoizedSlides.map(slide => parseInt(slide.id as string, 10));
      }
    });
  }, [memoizedSlides]);

  // Store functions and state in refs so they can be used in columnDefs without causing re-memoization
  const renderCheckboxForRowRef = React.useRef(null);
  const selectedSlideIdsRef = React.useRef(selectedSlideIds);
  const handleCheckboxChangeCallbackRef = React.useRef(null);

  // Keep ref in sync with state
  selectedSlideIdsRef.current = selectedSlideIds;

  const handleCheckboxChangeCallback = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const id = parseInt(e.target.value, 10);

    setSelectedSlideIds(prevArray => {
      if (prevArray.includes(id)) {
        return prevArray.filter(slideId => slideId !== id);
      } else {
        return [...prevArray, id];
      }
    });
  }, []);

  // Store callback in ref for use in columnDefs
  handleCheckboxChangeCallbackRef.current = handleCheckboxChangeCallback;

  const renderBulkCheckbox = useCallback(() => {
    const container = document.getElementById('select-all');
    if (!container) return;

    const label = (
      <span className="sr-only">
        {(bulkChecked ? 'Unselect' : 'Select').concat(' all records on this page')}
      </span>
    );

    ReactDOM.render(
      <Checkbox
        checked={bulkChecked === 'all'}
        handleChange={handleBulkCheckboxChangeCallback}
        id="select-all"
        indeterminate={bulkChecked === 'some'}
        input={{ name: 'select-all', value: 'select-all' }}
        label={label}
      />,
      container,
    );
  }, [bulkChecked, handleBulkCheckboxChangeCallback]);

  const renderCheckboxForRow = useCallback(({ checked, id, target }) => {
    if (!target) return;

    const label = (
      <span className="sr-only">
        {checked ? 'Unselect row' : 'Select row'}
      </span>
    );

    ReactDOM.render(
      <Checkbox
        checked={checked}
        handleChange={handleCheckboxChangeCallback}
        id={`bulk-slide-${id}`}
        input={{ name: 'bulk-slide', value: id.toString() }}
        label={label}
      />,
      target,
    );
  }, [handleCheckboxChangeCallback]);

  // Store the render function in ref for use in createdCell
  renderCheckboxForRowRef.current = renderCheckboxForRow;

  // Callback for DataTable initialization - use refs to avoid recreating this function
  const handleInitComplete = useCallback(() => {
    renderBulkCheckbox();
    // Render all checkboxes on initial load using ref to get current state
    memoizedSlides.forEach((slide) => {
      const target = document.getElementById(`bulk-slide-${slide.id}`);
      if (target && renderCheckboxForRowRef.current) {
        renderCheckboxForRowRef.current({
          checked: selectedSlideIdsRef.current.includes(parseInt(slide.id as string, 10)),
          id: slide.id,
          target,
        });
      }
    });
  }, [renderBulkCheckbox, memoizedSlides]);

  // Update bulk checkbox when bulkChecked state changes
  useEffect(() => {
    renderBulkCheckbox();
  }, [renderBulkCheckbox]);

  // Update individual checkboxes when selectedSlideIds changes
  useEffect(() => {
    memoizedSlides.forEach((slide) => {
      const target = document.getElementById(`bulk-slide-${slide.id}`);
      if (target) {
        renderCheckboxForRow({
          checked: selectedSlideIds.includes(parseInt(slide.id as string, 10)),
          id: slide.id,
          target,
        });
      }
    });
  }, [selectedSlideIds, memoizedSlides, renderCheckboxForRow]);

  // Update form value when selectedSlideIds changes (separate effect to avoid setValue dependency issues)
  useEffect(() => {
    setValue('slideIds', selectedSlideIds.join(', '));
  }, [selectedSlideIds, setValue]);

  // Memoize columnDefs with empty deps so it's created once and never changes
  const columnDefs = useMemo(() => [
    {
      targets: 0,
      title: '<div id="select-all" />',
      sortable: false,
      searchable: false,
      data: null,
      createdCell: (td, cellData, rowData) => {
        $(td).attr({
          id: `bulk-slide-${rowData.id}`,
          'data-title': rowData.title,
        });

        // Render checkbox using refs to avoid dependency on changing state
        const isChecked = selectedSlideIdsRef.current.includes(rowData.id);
        const label = (
          <span className="sr-only">
            {isChecked ? 'Unselect row' : 'Select row'}
          </span>
        );

        ReactDOM.render(
          <Checkbox
            checked={isChecked}
            handleChange={handleCheckboxChangeCallbackRef.current}
            id={`bulk-slide-${rowData.id}`}
            input={{ name: 'bulk-slide', value: rowData.id.toString() }}
            label={label}
          />,
          td,
        );
      },
    },
    {
      targets: 1,
      title: 'Slide #',
    },
    {
      targets: 2,
    },
  ], []);

  return (
    <DataTable
      columns={['id', 'position', 'title']}
      columnDefs={columnDefs}
      defaultOrder={[1, 'asc']}
      handleResourcesManually
      info={false}
      initComplete={handleInitComplete}
      paging={false}
      resources={memoizedSlides}
    />
  );
};

export default SelectSlideTable;
